import {
  autoUpdate,
  type ComputePositionConfig,
  flip,
  shift,
  useFloating,
  useTransitionStyles,
} from "@floating-ui/react";
import type { Override, PickRequired } from "@schema-benchmarks/utils";
import { mergeRefs } from "@schema-benchmarks/utils/react";
import { radEventListeners } from "rad-event-listeners";
import {
  type ComponentProps,
  type ElementType,
  type HTMLAttributes,
  type JSX,
  type ReactNode,
  type Ref,
  type RefCallback,
  useEffect,
  useState,
} from "react";
import bem from "react-bem-helper";

import { ButtonGroup } from "#src/shared/components/button";
import { ConsoleWriter } from "#src/shared/components/consolewriter";
import { useIdDefault } from "#src/shared/hooks/use-id-default";
import { useMediaQuery } from "#src/shared/hooks/use-media-query.ts";

const cls = bem("tooltip");

type TooltipableComponentProps = {
  ref: RefCallback<HTMLElement>;
  tooltip?: string | RichTooltipProps;
} & Pick<
  HTMLAttributes<HTMLElement>,
  "id" | "popoverTarget" | "popoverTargetAction" | "aria-labelledby"
>;

export interface TooltipOpts extends ComputePositionConfig {
  /** Require a tooltip prop to be set */
  required?: boolean;
  /** Delay before showing the tooltip */
  delay?: number;
}

export interface RichTooltipProps {
  subhead?: string;
  supporting: ReactNode;
  actions?: ReactNode;
  actionsLabel?: string;
}

export interface TooltipProps {
  tooltip?: string | RichTooltipProps;
  tooltipOpts?: ComputePositionConfig;
  popoverRef?: Ref<HTMLElement>;
  id?: string;
}

let currentId = "";

// https://tkdodo.eu/blog/tooltip-components-should-not-exist

export function withTooltip<
  TComp extends ElementType<PickRequired<TooltipableComponentProps, "tooltip">>,
>(
  Component: TComp,
  opts: Override<TooltipOpts, { required: true }>,
): (props: Override<ComponentProps<TComp>, PickRequired<TooltipProps, "tooltip">>) => JSX.Element;
export function withTooltip<TComp extends ElementType<TooltipableComponentProps>>(
  Component: TComp,
  opts?: TooltipOpts,
): (props: Override<ComponentProps<TComp>, TooltipProps>) => JSX.Element;
export function withTooltip<TComp extends ElementType<TooltipableComponentProps>>(
  Component: TComp,
  { delay = 1000, ...opts }: TooltipOpts = {},
) {
  return function WithTooltip({
    tooltip,
    tooltipOpts,
    id: idProp,
    ref,
    popoverRef: popoverRefProp,
    ...props
  }: Override<ComponentProps<TComp>, TooltipProps>) {
    const id = useIdDefault(idProp);
    const tooltipId = `${id}-tooltip`;
    const [open, setOpen] = useState(false);
    const { refs, floatingStyles, context } = useFloating({
      open,
      whileElementsMounted: autoUpdate,
      middleware: [shift(), flip()],
      ...opts,
      ...tooltipOpts,
    });
    const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)", false);
    const { styles } = useTransitionStyles(context, {
      duration: 75,
      initial: {
        opacity: 0,
        transform: prefersReducedMotion ? "scale(1)" : "scale(0.8)",
        transitionTimingFunction: "var(--enter-curve)",
      },
      open: {
        opacity: 1,
        transform: "scale(1)",
      },
    });
    const [targetRef, setTargetRef] = useState<HTMLElement | null>(null);
    const [popoverRef, setPopoverRef] = useState<HTMLElement | null>(null);
    useEffect(() => {
      if (targetRef && popoverRef && tooltip) {
        let timeout: ReturnType<typeof setTimeout> | undefined;
        function open(immediate = false) {
          clearTimeout(timeout);
          timeout = setTimeout(
            () => {
              popoverRef?.showPopover();
              currentId = id;
            },
            currentId || immediate ? 0 : delay,
          );
        }
        function close() {
          clearTimeout(timeout);
          popoverRef?.hidePopover();
          setTimeout(() => {
            if (currentId === id) {
              currentId = "";
            }
          }, 1000);
        }
        const unsubTarget = radEventListeners(targetRef, {
          mouseenter(_event, signal) {
            open();
            const unsubDoc = radEventListeners(
              document,
              {
                mousemove(event) {
                  if (
                    !targetRef?.contains(event.target as Node) &&
                    !popoverRef?.contains(event.target as Node)
                  ) {
                    unsubDoc();
                    close();
                  }
                },
              },
              { signal },
            );
          },
          focus(event) {
            event.preventDefault();
            open(true);
          },
          blur: close,
          keydown(event) {
            if (event.key === "Escape") close();
          },
        });
        return () => {
          unsubTarget();
          close();
        };
      }
      return;
    }, [targetRef, popoverRef, tooltip, id]);
    return (
      <>
        <Component
          // @ts-expect-error union nastiness
          {...(props as any)}
          id={id}
          tooltip={tooltip}
          ref={mergeRefs<HTMLElement>(ref, refs.setReference, setTargetRef)}
          {...(tooltip
            ? ({
                popoverTargetAction: "show",
                "aria-labelledby": tooltipId,
                popoverTarget: tooltipId,
              } satisfies HTMLAttributes<HTMLElement>)
            : {})}
        />
        {tooltip && (
          <div
            role="tooltip"
            ref={mergeRefs(setPopoverRef, refs.setFloating, popoverRefProp)}
            popover="hint"
            id={tooltipId}
            style={floatingStyles}
            {...cls({
              modifiers: { rich: typeof tooltip === "object" },
            })}
            onBeforeToggle={(e) => {
              setOpen(e.newState === "open");
            }}
          >
            <div {...cls("content")} style={styles}>
              {typeof tooltip === "string" ? (
                // we want tooltips to be visible quickly, so the typing should be fast
                <ConsoleWriter hidden={!open} delay={0} interval={35}>
                  {tooltip}
                </ConsoleWriter>
              ) : (
                <>
                  {tooltip.subhead && (
                    <h6
                      {...cls({
                        element: "subhead",
                        extra: "typo-caption",
                      })}
                    >
                      <ConsoleWriter hidden={!open} delay={0} interval={35}>
                        {tooltip.subhead}
                      </ConsoleWriter>
                    </h6>
                  )}
                  <div {...cls("supporting")}>{tooltip.supporting}</div>
                  {tooltip.actions && (
                    <ButtonGroup {...cls("actions")} ariaLabel={tooltip.actionsLabel ?? "Actions"}>
                      {tooltip.actions}
                    </ButtonGroup>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </>
    );
  };
}
